import React, { useState } from 'react';
import styled from 'styled-components';
import salonData from '../../data/salonData';
import { Testimonial } from '../../types/salonTypes';

export interface TestimonialsSectionProps {
  testimonials: Array<{
    id: string;
    name: string;
    rating: number;
    comment: string;
    service: string;
    date: string;
  }>;
}

const TestimonialsSection: React.FC<TestimonialsSectionProps> = ({ testimonials }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  
  // Testimonials değerini kontrol et ve varsayılan bir değer ata
  const safeTestimonials = Array.isArray(testimonials) ? testimonials : [];

  // Eğer testimonials dizisi boşsa, bileşeni render etme
  if (safeTestimonials.length === 0) {
    console.log('Testimonials veri yok veya boş dizi');
    return null;
  }

  const handlePrev = () => {
    setActiveIndex((prevIndex) => 
      prevIndex === 0 ? safeTestimonials.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setActiveIndex((prevIndex) => 
      prevIndex === safeTestimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  return (
    <TestimonialsSectionContainer>
      <TestimonialsWaveBg />
      <div className="container">
        <SectionHeader>
          <SectionTitle>Müşteri Yorumları</SectionTitle>
          <SectionSubtitle>Müşterilerimizin bizim hakkımızda söyledikleri</SectionSubtitle>
        </SectionHeader>
        
        <TestimonialsWrapper>
          <DesktopView>
            {safeTestimonials.map((testimonial: Testimonial) => (
              <TestimonialCard key={testimonial.id}>
                <TestimonialContent>
                  <ServiceTag>{testimonial.service}</ServiceTag>
                  <TestimonialText>{testimonial.comment}</TestimonialText>
                </TestimonialContent>
                <TestimonialFooter>
                  <TestimonialRating>
                    {Array.from({ length: 5 }).map((_, index: number) => (
                      <StarDot 
                        key={index} 
                        filled={index < testimonial.rating}
                      />
                    ))}
                  </TestimonialRating>
                  <TestimonialAuthor>
                    <TestimonialName>{testimonial.name}</TestimonialName>
                  </TestimonialAuthor>
                </TestimonialFooter>
              </TestimonialCard>
            ))}
          </DesktopView>

          <MobileView>
            <NavigationButton onClick={handlePrev} direction="left">
              &lt;
            </NavigationButton>
            
            <TestimonialCarousel>
              {safeTestimonials.map((testimonial: Testimonial, index: number) => (
                <TestimonialSlide 
                  key={testimonial.id} 
                  active={index === activeIndex}
                >
                  <TestimonialContent>
                    <ServiceTag>{testimonial.service}</ServiceTag>
                    <TestimonialText>{testimonial.comment}</TestimonialText>
                  </TestimonialContent>
                  <TestimonialFooter>
                    <TestimonialRating>
                      {Array.from({ length: 5 }).map((_, starIndex: number) => (
                        <StarDot 
                          key={starIndex} 
                          filled={starIndex < testimonial.rating}
                        />
                      ))}
                    </TestimonialRating>
                    <TestimonialAuthor>
                      <TestimonialName>{testimonial.name}</TestimonialName>
                    </TestimonialAuthor>
                  </TestimonialFooter>
                </TestimonialSlide>
              ))}
            </TestimonialCarousel>
            
            <NavigationButton onClick={handleNext} direction="right">
              &gt;
            </NavigationButton>
          </MobileView>

          <SliderDots>
            {safeTestimonials.map((_: any, index: number) => (
              <SliderDot 
                key={index} 
                active={index === activeIndex}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </SliderDots>
        </TestimonialsWrapper>
      </div>
    </TestimonialsSectionContainer>
  );
};

const TestimonialsSectionContainer = styled.section`
  padding: 100px 0;
  background: linear-gradient(135deg, #f9f9f9 0%, #f0f0f0 100%);
  position: relative;
  overflow: hidden;
`;

const TestimonialsWaveBg = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='smallGrid' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 20 0 L 0 0 0 20' fill='none' stroke='%23e0e0e0' stroke-width='0.5'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23smallGrid)'/%3E%3C/svg%3E");
  opacity: 0.3;
  z-index: 0;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
  position: relative;
  z-index: 1;
`;

const SectionTitle = styled.h2`
  margin-bottom: 15px;
  position: relative;
  font-size: 2.2rem;
  font-weight: 700;
  display: inline-block;
  
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 3px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    margin: 15px auto 0;
    border-radius: 3px;
  }
`;

const SectionSubtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const TestimonialsWrapper = styled.div`
  position: relative;
  z-index: 1;
`;

const DesktopView = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 30px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileView = styled.div`
  display: none;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const TestimonialCarousel = styled.div`
  width: 100%;
  max-width: 500px;
  position: relative;
  overflow: hidden;
  min-height: 280px;
`;

const TestimonialSlide = styled.div<{ active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  opacity: ${({ active }) => (active ? 1 : 0)};
  transform: translateX(${({ active }) => (active ? 0 : '50px')});
  transition: all 0.5s ease;
  pointer-events: ${({ active }) => (active ? 'all' : 'none')};
`;

const NavigationButton = styled.button<{ direction: string }>`
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  margin: 0 15px;
  transition: all 0.2s;
  font-size: 16px;
  font-weight: bold;
  
  &:hover {
    transform: scale(1.1);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const SliderDots = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const SliderDot = styled.div<{ active: boolean }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin: 0 5px;
  cursor: pointer;
  transition: all 0.3s;
  background: ${({ active, theme }) => 
    active 
      ? `linear-gradient(45deg, ${theme.colors.primary}, ${theme.colors.secondary})` 
      : '#ddd'};
  
  &:hover {
    transform: scale(1.2);
  }
`;

const TestimonialCard = styled.div`
  background-color: white;
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const TestimonialContent = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const ServiceTag = styled.div`
  display: inline-block;
  padding: 6px 12px;
  background: linear-gradient(45deg, ${({ theme }) => theme.colors.primary}15, ${({ theme }) => theme.colors.secondary}15);
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 20px;
  font-size: 0.85rem;
  margin-bottom: 15px;
  font-weight: 600;
`;

const TestimonialText = styled.p`
  color: #444;
  font-style: italic;
  line-height: 1.7;
  font-size: 1rem;
  position: relative;
  padding-left: 20px;
  
  &:before {
    content: '"';
    position: absolute;
    top: 0;
    left: 0;
    font-size: 1.8rem;
    color: ${({ theme }) => theme.colors.primary}40;
    font-family: Georgia, serif;
    line-height: 1;
  }
`;

const TestimonialFooter = styled.div`
  display: flex;
  flex-direction: column;
`;

const TestimonialRating = styled.div`
  display: flex;
  margin-bottom: 15px;
`;

const StarDot = styled.div<{ filled: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-right: 5px;
  background-color: ${({ filled }) => filled ? '#FFD700' : '#e0e0e0'};
`;

const TestimonialAuthor = styled.div`
  display: flex;
  flex-direction: column;
`;

const TestimonialName = styled.span`
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
`;

export default TestimonialsSection; 